import type { RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { accountRoute } from "@/modules/account/router";
import { addressesRoute } from "@/modules/addresses/router";
import { adminNotificationsRoute } from "@/modules/admin-notifications/router";
import { analyticsRoute } from "@/modules/analytics/router";
import { bookingsRoute } from "@/modules/bookings/router";
import { buildingsRoute } from "@/modules/buildings/router";
import { calendarRoute } from "@/modules/calendar/router";
import { chatsRoute } from "@/modules/chats/router";
import { communityFeedRoute } from "@/modules/community-feed/router";
import { helpCategoriesRoute, interestGroupsRoute } from "@/modules/community-groups/router";
import { companyRoute } from "@/modules/companies/router";
import { documentsRoute } from "@/modules/documents/router";
import { emailUnsubscribeRoute } from "@/modules/email-unsubscribe/router";
import { eventsRoute } from "@/modules/events/router";
import { projectHomeRoute } from "@/modules/home/router";
import { onboardingScreensRoute } from "@/modules/onboarding-screens/router";
import { portfolioRoute } from "@/modules/portfolio/router";
import { projectsRoute } from "@/modules/project/router";
import { RedirectToRoute } from "@/modules/redirects/RedirectToRoute";
import { SlugRedirect } from "@/modules/redirects/SlugRedirect";
import { deslug } from "@/modules/redirects/utils";
import { residentNotificationsRoute } from "@/modules/resident-notifications/router";
import { rolesRoute } from "@/modules/roles/router";
import { servicePartnersRoute } from "@/modules/service-partners/router";
import { surveysRoute } from "@/modules/surveys/router";
import { alertsRoute } from "@/modules/system-settings/alerts/router";
import { automatedSurveysRoute } from "@/modules/system-settings/automated-surveys/router";
import { platformGroupsRoute } from "@/modules/system-settings/platform-groups/router";
import { projectConnectionsRoute } from "@/modules/system-settings/project-connections/router";
import { ticketCategoriesRoute } from "@/modules/ticket-categories/router";
import { ticketStatusesRoute } from "@/modules/ticket-statuses/router";
import { ticketsRoute } from "@/modules/tickets/router";
import { usersRoute } from "@/modules/users/router";
import { routes } from "@/routes";

type ModuleRoute = {
  path: string;
  children: RouteObject[];
};

const projectRoutes = [
  { path: routes.adminNotifications.ROOT, children: adminNotificationsRoute },
  { path: routes.residentNotifications.ROOT, children: residentNotificationsRoute },
  { path: routes.ticketCategories.ROOT, children: ticketCategoriesRoute },
  { path: routes.ticketStatuses.ROOT, children: ticketStatusesRoute },
  { path: routes.tickets.ROOT, children: ticketsRoute },
  { path: routes.events.ROOT, children: eventsRoute },
  { path: routes.calendar.ROOT, children: calendarRoute },
  { path: routes.bookings.ROOT, children: bookingsRoute },
  { path: routes.roles.ROOT, children: rolesRoute },
  { path: routes.helpCategories.ROOT, children: helpCategoriesRoute },
  { path: routes.interestGroups.ROOT, children: interestGroupsRoute },
  { path: routes.servicePartners.ROOT, children: servicePartnersRoute },
  { path: routes.documents.ROOT, children: documentsRoute },
  { path: routes.analytics.ROOT, children: analyticsRoute },
  { path: routes.addresses.ROOT, children: addressesRoute },
  { path: routes.communityFeed.ROOT, children: communityFeedRoute },
  { path: routes.projects.ROOT, children: projectsRoute },
  { path: routes.buildings.ROOT, children: buildingsRoute },
  { path: routes.companies.ROOT, children: companyRoute },
  { path: routes.onboardingScreens.ROOT, children: onboardingScreensRoute },
  { path: routes.surveys.ROOT, children: surveysRoute },
  { path: routes.chats.ROOT, children: chatsRoute },
  { path: routes.users.ROOT, children: usersRoute },
  { path: routes.home.ROOT, children: projectHomeRoute },
  { path: routes.emailUnsubscribe.ROOT, children: emailUnsubscribeRoute },
] satisfies ModuleRoute[];

const portfolioRoutes = [
  { path: routes.projectConnections.ROOT, children: projectConnectionsRoute },
  { path: routes.automatedSurveys.ROOT, children: automatedSurveysRoute },
  { path: routes.platformGroups.ROOT, children: platformGroupsRoute },
  { path: routes.alerts.ROOT, children: alertsRoute },
  { path: routes.portfolio.ROOT, children: portfolioRoute },
  { path: routes.account.ROOT, children: accountRoute },
] satisfies ModuleRoute[];

const slugRedirectRoutes: RouteObject[] = [
  ...projectRoutes.map(({ path }) => ({
    path: deslug(path),
    element: <SlugRedirect />,
  })),
  { path: "/home", element: <SlugRedirect /> },
];

export const protectedRoutes = [
  ...portfolioRoutes,
  {
    path: "/@/:slug",
    children: [
      {
        errorElement: <ErrorPage />,
        children: [
          ...projectRoutes,
          { path: "", element: <RedirectToRoute withSlug to={routes.home.home} /> },
          { path: "*", element: <ErrorPage status={404} /> },
        ],
      },
    ],
  },
  ...slugRedirectRoutes,
  { path: "*", element: <ErrorPage status={404} /> },
] satisfies RouteObject[];
